import React, { useState } from 'react';
import MainLayout from '../../components/Layout/MainLayout';
import { PageCard, BtnPrimary, BtnSecondary, FormField, FormInput, FormSelect, Alert } from '../../components/UI/UIComponents';
import './Settings.css';

const TABS = ['General', 'Audio & Voice', 'Language', 'Advanced'];

const Settings = () => {
  const [tab,      setTab]      = useState('General');
  const [saved,    setSaved]    = useState(false);
  const [settings, setSettings] = useState({
    email:       localStorage.getItem('hostEmail')    || 'rukucse11@gmail.com',
    displayName: localStorage.getItem('displayName')  || '',
    defaultLang: localStorage.getItem('defaultLang')  || 'en-US',
    voiceSpeed:  localStorage.getItem('voiceSpeed')   || '1',
    voicePitch:  localStorage.getItem('voicePitch')   || '1',
    voiceEN:     localStorage.getItem('voiceEN')      || 'Google US English',
    voiceJA:     localStorage.getItem('voiceJA')      || 'Google 日本語',
    serverUrl:   localStorage.getItem('serverUrl')    || process.env.REACT_APP_API_URL || `http://localhost:${process.env.REACT_APP_PORT || 5001}`,
    clientUrl:   localStorage.getItem('clientUrl')    || process.env.REACT_APP_CLIENT_URL || `http://localhost:${process.env.REACT_APP_CLIENT_PORT || 3006}`,
    enableLogs:  localStorage.getItem('enableLogs')   !== 'false',
    autoStart:   localStorage.getItem('autoStart')    === 'true',
  });

  const update = (key, val) => setSettings(s => ({...s, [key]: val}));

  const handleSave = () => {
    Object.entries(settings).forEach(([k, v]) => localStorage.setItem(k, v));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleReset = () => {
    if (window.confirm('Reset all settings to defaults?')) {
      localStorage.clear();
      window.location.reload();
    }
  };

  return (
    <MainLayout pageTitle="Settings" pageSubtitle="Configure your AI interpretation environment">
      <div className="settings-page">

        {/* Tab bar */}
        <div className="settings-tabs">
          {TABS.map(t => (
            <button key={t} className={`settings-tab${tab===t?' active':''}`} onClick={()=>setTab(t)}>{t}</button>
          ))}
        </div>

        {saved && <Alert type="success">Settings saved successfully!</Alert>}

        {/* General */}
        {tab === 'General' && (
          <PageCard title="General Settings" subtitle="Your account and display preferences">
            <div className="settings-grid">
              <FormField label="Host Email (Zoom account)" required>
                <FormInput
                  type="email"
                  placeholder="your@email.com"
                  value={settings.email}
                  onChange={e => update('email', e.target.value)}
                />
                <p className="settings-hint">Used to create meetings via the Zoom API.</p>
              </FormField>
              <FormField label="Default Display Name">
                <FormInput
                  type="text"
                  placeholder="Your name"
                  value={settings.displayName}
                  onChange={e => update('displayName', e.target.value)}
                />
              </FormField>
              <FormField label="Default Language">
                <FormSelect value={settings.defaultLang} onChange={e=>update('defaultLang', e.target.value)}>
                  <option value="en-US">English (United States)</option>
                  <option value="ja-JP">Japanese (Japan)</option>
                </FormSelect>
              </FormField>
            </div>
          </PageCard>
        )}

        {/* Audio & Voice */}
        {tab === 'Audio & Voice' && (
          <PageCard title="Audio & Voice Settings" subtitle="Configure speech recognition and synthesis">
            <div className="settings-grid">
              <FormField label="Text-to-Speech Speed">
                <div className="settings-slider-wrap">
                  <input type="range" min="0.5" max="2" step="0.1" value={settings.voiceSpeed}
                    onChange={e=>update('voiceSpeed', e.target.value)} className="settings-slider"/>
                  <span className="settings-slider-val">{parseFloat(settings.voiceSpeed).toFixed(1)}x</span>
                </div>
              </FormField>
              <FormField label="Text-to-Speech Pitch">
                <div className="settings-slider-wrap">
                  <input type="range" min="0" max="2" step="0.1" value={settings.voicePitch}
                    onChange={e=>update('voicePitch', e.target.value)} className="settings-slider"/>
                  <span className="settings-slider-val">{parseFloat(settings.voicePitch).toFixed(1)}</span>
                </div>
              </FormField>
              <FormField label="English TTS Voice Name">
                <FormInput type="text" value={settings.voiceEN} onChange={e=>update('voiceEN', e.target.value)} placeholder="Google US English"/>
                <p className="settings-hint">Must match a voice available in your browser.</p>
              </FormField>
              <FormField label="Japanese TTS Voice Name">
                <FormInput type="text" value={settings.voiceJA} onChange={e=>update('voiceJA', e.target.value)} placeholder="Google 日本語"/>
              </FormField>
            </div>

            {/* Test audio panel */}
            <div className="settings-audio-test">
              <p className="settings-audio-test-title">🔊 Test your voice settings</p>
              <div style={{display:'flex',gap:12,marginTop:12}}>
                <BtnSecondary onClick={() => {
                  const u = new SpeechSynthesisUtterance('Hello! This is a test of the English voice.');
                  u.rate = parseFloat(settings.voiceSpeed);
                  u.pitch = parseFloat(settings.voicePitch);
                  window.speechSynthesis.speak(u);
                }}>▶ Test English</BtnSecondary>
                <BtnSecondary onClick={() => {
                  const u = new SpeechSynthesisUtterance('こんにちは！日本語の音声テストです。');
                  u.rate = parseFloat(settings.voiceSpeed);
                  u.pitch = parseFloat(settings.voicePitch);
                  window.speechSynthesis.speak(u);
                }}>▶ Test Japanese</BtnSecondary>
              </div>
            </div>
          </PageCard>
        )}

        {/* Language */}
        {tab === 'Language' && (
          <PageCard title="Language Configuration" subtitle="Translation and recognition settings">
            <div className="settings-lang-info">
              <div className="settings-lang-pair">
                <div className="slp-lang">
                  <div className="slp-flag">🇺🇸</div>
                  <div className="slp-name">English</div>
                  <div className="slp-code">en-US</div>
                </div>
                <div className="slp-arrow">⇄</div>
                <div className="slp-lang">
                  <div className="slp-flag">🇯🇵</div>
                  <div className="slp-name">Japanese</div>
                  <div className="slp-code">ja-JP</div>
                </div>
              </div>
              <p className="settings-lang-note">Currently supported: English ↔ Japanese. Additional language pairs are planned for future releases.</p>
            </div>

            <div className="settings-toggle-section">
              <div className="settings-toggle-item">
                <div>
                  <p className="settings-toggle-title">Auto-start Speech Recognition</p>
                  <p className="settings-toggle-desc">Automatically activate the microphone when joining a meeting.</p>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" checked={settings.autoStart} onChange={e=>update('autoStart', e.target.checked)}/>
                  <span className="toggle-track"/>
                </label>
              </div>
            </div>
          </PageCard>
        )}

        {/* Advanced */}
        {tab === 'Advanced' && (
          <PageCard title="Advanced Settings" subtitle="Server URLs and developer options">
            <div className="settings-grid">
              <FormField label="Socket Server URL">
                <FormInput type="url" value={settings.serverUrl} onChange={e=>update('serverUrl', e.target.value)} placeholder="http://localhost:5001"/>
                <p className="settings-hint">Node.js + Socket.IO server address.</p>
              </FormField>
              <FormField label="Zoom SDK Client URL">
                <FormInput type="url" value={settings.clientUrl} onChange={e=>update('clientUrl', e.target.value)} placeholder="http://localhost:3006"/>
                <p className="settings-hint">URL where the client meeting app is hosted.</p>
              </FormField>
            </div>
            <div className="settings-toggle-section">
              <div className="settings-toggle-item">
                <div>
                  <p className="settings-toggle-title">Enable Server Logging</p>
                  <p className="settings-toggle-desc">Write all events to combined.log on the server.</p>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" checked={settings.enableLogs} onChange={e=>update('enableLogs', e.target.checked)}/>
                  <span className="toggle-track"/>
                </label>
              </div>
            </div>
            <div className="settings-danger-zone">
              <p className="settings-danger-title">⚠️ Danger Zone</p>
              <BtnSecondary onClick={handleReset}>Reset all settings to defaults</BtnSecondary>
            </div>
          </PageCard>
        )}

        {/* Save bar */}
        <div className="settings-save-bar">
          <BtnPrimary onClick={handleSave}>💾  Save Changes</BtnPrimary>
          <BtnSecondary onClick={() => window.location.reload()}>Discard</BtnSecondary>
        </div>

      </div>
    </MainLayout>
  );
};

export default Settings;
